import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useFetch } from '../../hooks/useFetch';
import { Spinner, ErrorBanner, Badge } from '../../components/ui';
import { fmt } from '../../utils/format';
import api from '../../utils/api';

function MetricCard({ label, value, sub, icon, color }) {
  return (
    <div className="metric-card">
      <div className="metric-label" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        {icon && <i className={`ti ${icon}`} style={{ fontSize: 14, color: color || 'var(--text-tertiary)' }} />}
        {label}
      </div>
      <div className="metric-value" style={color ? { color } : {}}>{value}</div>
      {sub && <div className="metric-sub">{sub}</div>}
    </div>
  );
}

function StatusDot({ status }) {
  const color = status === 'success' ? '#4ade80' : status === 'error' ? '#ef4444' : '#f59e0b';
  return <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block', marginRight: 6 }} />;
}

export default function Dashboard() {
  const { data, loading, error, refetch } = useFetch('/api/dashboard');
  const navigate = useNavigate();

  const handleSyncAll = async () => {
    try {
      await Promise.allSettled([
        api.post('/api/integrations/shopify/sync'),
        api.post('/api/integrations/amazon/sync'),
      ]);
      refetch();
    } catch {}
  };

  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} onRetry={refetch} />;
  if (!data) return null;

  const { metrics, featureFlags, lookbackDays, materialHealth, recentOrders, fgAlertList, workOrderSummary, syncStatus } = data;

  return (
    <div>
      {/* Topbar actions */}
      <div className="page-header">
        <h1>Dashboard</h1>
        <div className="page-actions">
          <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
            {lookbackDays}-day velocity
          </span>
          <button className="btn btn-primary" onClick={handleSyncAll}>
            <i className="ti ti-refresh" /> Sync all
          </button>
        </div>
      </div>

      {/* Critical alerts */}
      {metrics.rawMaterialsCriticalCount > 0 && (
        <div className="alert alert-danger" style={{ marginBottom: 12, cursor: 'pointer' }}
          onClick={() => navigate('/raw-materials')}>
          <i className="ti ti-alert-triangle" />
          <div>
            <strong>{metrics.rawMaterialsCriticalCount} material{metrics.rawMaterialsCriticalCount > 1 ? 's' : ''} at critical level</strong>
            — immediate action needed.
            <span style={{ marginLeft: 6, textDecoration: 'underline' }}>View →</span>
          </div>
        </div>
      )}

      {metrics.fgOutOfStockCount > 0 && (
        <div className="alert alert-danger" style={{ marginBottom: 12, cursor: 'pointer' }}
          onClick={() => navigate('/products')}>
          <i className="ti ti-alert-circle" />
          <div>
            <strong>{metrics.fgOutOfStockCount} finished good{metrics.fgOutOfStockCount > 1 ? 's' : ''} out of stock</strong>
            — immediate action needed.
            <span style={{ marginLeft: 6, textDecoration: 'underline' }}>View →</span>
          </div>
        </div>
      )}

      {metrics.kitZeroCount > 0 && (
        <div className="alert alert-danger" style={{ marginBottom: 12, cursor: 'pointer' }}
          onClick={() => navigate('/kits')}>
          <i className="ti ti-packages" />
          <div>
            <strong>{metrics.kitZeroCount} kit{metrics.kitZeroCount > 1 ? 's' : ''} cannot be built</strong>
            — components out of stock.
            <span style={{ marginLeft: 6, textDecoration: 'underline' }}>View →</span>
          </div>
        </div>
      )}

      {metrics.fgLowCount > 0 && (
        <div className="alert alert-warning" style={{ marginBottom: 12, cursor: 'pointer' }}
          onClick={() => navigate('/products')}>
          <i className="ti ti-alert-triangle" />
          <div>
            <strong>{metrics.fgLowCount} SKU{metrics.fgLowCount > 1 ? 's' : ''} below low-stock threshold.</strong>
            <span style={{ marginLeft: 6, textDecoration: 'underline' }}>View →</span>
          </div>
        </div>
      )}

      {metrics.kitLowCount > 0 && (
        <div className="alert alert-warning" style={{ marginBottom: 12, cursor: 'pointer' }}
          onClick={() => navigate('/kits')}>
          <i className="ti ti-packages" />
          <div>
            <strong>{metrics.kitLowCount} kit{metrics.kitLowCount > 1 ? 's' : ''} below buildable quantity threshold.</strong>
            <span style={{ marginLeft: 6, textDecoration: 'underline' }}>View →</span>
          </div>
        </div>
      )}

      {featureFlags.enableBackorders && metrics.openBackorders > 0 && (
        <div className="alert alert-warning" style={{ marginBottom: 12, cursor: 'pointer' }}
          onClick={() => navigate('/backorders')}>
          <i className="ti ti-clock-pause" />
          <div>
            <strong>{metrics.openBackorders} open backorder{metrics.openBackorders > 1 ? 's' : ''}</strong>
            — customers waiting on stock.
            <span style={{ marginLeft: 6, textDecoration: 'underline' }}>View →</span>
          </div>
        </div>
      )}

      {/* KPI metrics */}
      <div className="metrics-grid">
        <MetricCard label="Finished goods value" icon="ti-coin" color="var(--blue)"
          value={fmt.currency(metrics.finishedGoodsValue)}
          sub={`${fmt.number(metrics.finishedGoodsUnits)} units · ${metrics.skuCount} SKUs`} />
        <MetricCard label="Avg gross margin" icon="ti-chart-bar" color="var(--green)"
          value={fmt.pct(metrics.avgGrossMarginPct)} sub="Across active SKUs" />
        <MetricCard label="Outstanding PO value" icon="ti-receipt"
          value={fmt.currency(metrics.outstandingPoValue)} sub="Open purchase orders" />
        <MetricCard label="Materials need attention" icon="ti-box"
          color={metrics.rawMaterialsAttentionCount > 0 ? 'var(--amber)' : undefined}
          value={metrics.rawMaterialsAttentionCount}
          sub={`${metrics.rawMaterialsCriticalCount} critical`} />
        {featureFlags.enableBackorders && (
          <MetricCard label="Open backorders" icon="ti-clock-pause"
            color={metrics.openBackorders > 0 ? 'var(--red)' : undefined}
            value={metrics.openBackorders ?? 0} sub="Awaiting fulfillment" />
        )}
        {featureFlags.enableWorkOrders && (
          <MetricCard label="Open work orders" icon="ti-tool"
            value={metrics.openWorkOrders ?? 0} sub="Planned + in progress" />
        )}
      </div>

      {/* Finished goods with active alerts */}
      <div style={{ marginBottom: 12 }}>
        <div className="card">
          <div className="card-title" style={{ display: 'flex', justifyContent: 'space-between' }}>
            Finished goods — active alerts
            <button className="btn btn-sm" onClick={() => navigate('/products')}>View all</button>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Name</th><th>Qty</th><th>SKU</th><th>Threshold</th><th>Status</th></tr>
              </thead>
              <tbody>
                {(fgAlertList || []).length === 0 ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: 16 }}>
                    <i className="ti ti-circle-check" style={{ marginRight: 6, color: '#4ade80' }} />
                    All finished goods above threshold
                  </td></tr>
                ) : (fgAlertList || []).map((p) => (
                  <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/products')}>
                    <td>{p.name}</td>
                    <td style={{ color: p.alert_status === 'out_of_stock' ? 'var(--red)' : 'var(--amber)', fontWeight: 600 }}>
                      {fmt.number(p.effective_qty)}
                    </td>
                    <td>{fmt.number(p.low_stock_threshold)}</td>
                    <td><Badge status={p.alert_status === 'out_of_stock' ? 'critical' : 'warning'} label={p.alert_status === 'out_of_stock' ? 'Out of stock' : 'Low'} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Raw material health */}
      <div style={{ marginBottom: 12 }}>
        <div className="card">
          <div className="card-title">Raw material health</div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Material</th><th>On hand</th><th>Minimum</th><th>Status</th></tr>
              </thead>
              <tbody>
                {materialHealth?.map((m) => (
                  <tr key={m.name} style={{ cursor: 'pointer' }} onClick={() => navigate('/raw-materials')}>
                    <td>{m.name}</td>
                    <td>{fmt.qty(m.quantity_on_hand, m.unit_of_measure)}</td>
                    <td>{fmt.qty(m.min_stock_level, m.unit_of_measure)}</td>
                    <td><Badge status={m.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {/* Work orders or sync status */}
        <div className="card">
          {featureFlags.enableWorkOrders && workOrderSummary?.length > 0 ? (
            <>
              <div className="card-title" style={{ display: 'flex', justifyContent: 'space-between' }}>
                Open work orders
                <button className="btn btn-sm" onClick={() => navigate('/work-orders')}>View all</button>
              </div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>SKU</th><th>Qty</th><th>Status</th></tr></thead>
                  <tbody>
                    {workOrderSummary.map((wo) => (
                      <tr key={wo.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/work-orders/${wo.id}`)}>
                        <td>{wo.sku}</td><td>{wo.quantity}</td><td><Badge status={wo.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <>
              <div className="card-title">Sync status</div>
              {syncStatus?.map((s) => (
                <div key={s.channel} style={{ display: 'flex', alignItems: 'center', marginBottom: 8, fontSize: 13 }}>
                  <StatusDot status={s.status} />
                  <span style={{ textTransform: 'capitalize', fontWeight: 500, marginRight: 8 }}>{s.channel}</span>
                  <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>
                    {s.finished_at ? fmt.dateTime(s.finished_at) : 'Never synced'}
                  </span>
                </div>
              ))}
              {!syncStatus?.length && <p style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>No sync history yet.</p>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
